import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { catchError, map, of, throwError } from 'rxjs';
import { NotificationService } from './notification.service';
import { FirmSelectionService } from './firm-selection.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  readonly baseUrl: string = environment.apiBaseUrl;

  constructor(
    private httpClient: HttpClient,
    private notification: NotificationService,
    private router: Router,
    private firmSelectionService: FirmSelectionService
  ) { }

  get isAuthenticated(): boolean {
    return !!sessionStorage.getItem('token');
  }

  get headers(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "Accept": "application/json"
    });
  }

  // COMMON OPTIONS FOR ALL API REQUESTS
  get options() {
    return {
      headers: this.headers,
      withCredentials: true  // IMPORTANT FOR SANCTUM
    };
  }

  // LOGIN API
  login(body: object) {
    return this.httpClient.post(
      this.baseUrl + 'login',
      body,
      this.options
    ).pipe(
      catchError((errors: HttpErrorResponse) => {
        this.formatError(errors);
        return throwError(() => errors);
      }),
      map((res: any) => res)
    );
  }

  // SIGNUP API
  signup(body: object) {
    return this.httpClient.post(
      this.baseUrl + 'signup',
      body,
      this.options
    ).pipe(
      catchError((errors: HttpErrorResponse) => {
        this.formatError(errors);
        return throwError(() => errors);
      }),
      map((res: any) => res)
    );
  }

  private formatError(errors: HttpErrorResponse) {
    if (!errors.error.errors) return;

    const errorsData = errors.error.errors;

    Object.entries(errorsData).forEach(([key, value]) => {
      const message = `Field: ${key}, Error: ${value}`;
      this.notification.showError(message, 'Validation Error');
    });
  }

  // SEND OTP
  sendOtpLogin(data: { user_type?: string; owner_email?: string; mobilenumber?: string; email?: string }) {
    return this.httpClient.post(
      `${this.baseUrl}auth/send-otp-login`,
      data,
      this.options
    );
  }

  // VERIFY OTP
  verifyOtpLogin(data: { user_type?: string; owner_email?: string; mobilenumber?: string; email?: string; otp: string }) {
    return this.httpClient.post(
      `${this.baseUrl}auth/verify-otp-login`,
      data,
      this.options
    );
  }

  getUserRole(): string | null {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.role || null;
    } catch (error) {
      return null;
    }
  }

  isKalakaar(): boolean {
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.sub_role === 'Karigar';
    } catch (error) {
      return false;
    }
  }

  isCA(): boolean {
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.sub_role === 'Chartered Accountant';
    } catch (error) {
      return false;
    }
  }

  isGstStaff(): boolean {
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      if (decoded?.role === 'Staff' && 
          decoded?.sub_role !== 'Chartered Accountant' && 
          decoded?.sub_role !== 'Karigar' && 
          decoded?.sub_role !== 'Customer') {
        const gstStatus = decoded?.gst_status;
        return !!gstStatus && ['gst', 'registered', 'yes', '1', 'true'].includes(gstStatus.toLowerCase());
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  getUserId(): any | null {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.sub || null;
    } catch (error) {
      return null;
    }
  }

  getUserSubRole(): string | null {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.sub_role || null;
    } catch {
      return null;
    }
  }

  getUserStaffType(): string | null {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.staff_type || null;
    } catch {
      return null;
    }
  }


  isCustomer(): boolean {
    return this.getUserRole() === 'Staff' &&
      this.getUserSubRole() === 'Customer';
  }

  isStaffNonKarigar(): boolean {
    return this.getUserRole() === 'Staff' &&
      !['Karigar', 'Customer'].includes(this.getUserSubRole() ?? '');
  }

  /**
   * Clears login keys only. Does not wipe UI prefs (theme, column layouts).
   * Same for every panel — does not change GST / RM / Karigar / Admin routing while logged in.
   */
  clearAuthSession(): void {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('is_firm_restricted');
    this.firmSelectionService.clearSelectedFirm();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('supplierId');
  }

  /**
   * Invalidate JWT on the server (best effort), then always clear the browser session
   * and replace the current history entry with /login so Back cannot reopen a panel as logged-in.
   */
  logout(): void {
    const token = sessionStorage.getItem('token');
    const finish = () => {
      this.clearAuthSession();
      this.router.navigate(['/login'], { replaceUrl: true });
    };

    if (!token) {
      finish();
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    });

    this.httpClient.post(
      this.baseUrl + 'logout',
      {},
      { headers, withCredentials: true }
    ).pipe(
      catchError(() => of(null))
    ).subscribe(() => finish());
  }

}

